import React, { useState, useEffect, useRef, useMemo } from "react";
import "./VerifyLiveTrade.css";
import "../../../newDashBoard/LiveTrades/LiveTrades.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import RefreshIcon from "../../../../assets/images/Dashboard/refreshImg.svg";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { useNavigate } from "react-router-dom";
import { globalCurrentPageTitle } from "../../../../store/globalstore";
import { useAtom } from "jotai";
import { useDashboardMarket } from "../../../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import { setCurrentTab } from "../../../../redux/links/linksSlice";
import { useDispatch } from "react-redux";
import { getSpotLiveTradeHideBelow } from "../../../sharedPages/newSetting/SpotLiveTradeSettings/spotLiveTradeHideBelow";
import SpotLiveTradeHideSelect from "../../../sharedPages/newSetting/SpotLiveTradeSettings/SpotLiveTradeHideSelect";

const MARKET_OPTIONS = [
  { key: "futures", label: "Futures" },
  { key: "spot", label: "Spot" },
];

const formatOpenDuration = (timestamp) => {
  if (timestamp == null || timestamp === "") return null;

  let start = Number(timestamp);
  if (!Number.isFinite(start)) {
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return null;
    start = parsed;
  }
  if (start <= 0) return null;
  // Some exchanges return seconds instead of ms.
  if (start < 1e12) start *= 1000;

  const totalMinutes = Math.max(0, Math.floor((Date.now() - start) / 60000));
  const years = Math.floor(totalMinutes / (60 * 24 * 365));
  const daysAfterYears = Math.floor(
    (totalMinutes % (60 * 24 * 365)) / (60 * 24)
  );
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (years > 0) return `${years}y ${daysAfterYears}d`;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0 || minutes > 0) return `${hours}h ${minutes}m`;
  return "< 1m";
};

/** Pick first usable epoch (rejects null / 0 / empty / pre-2000). Avoids 01-01-1970. */
const pickTimestamp = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    let n = Number(value);
    if (!Number.isFinite(n)) {
      n = Date.parse(value);
      if (!Number.isFinite(n)) continue;
    }
    if (n <= 0) continue;
    const ms = n < 1e12 ? n * 1000 : n;
    if (ms < 946684800000) continue; // before 2000-01-01
    return value;
  }
  return null;
};

/**
 * Open duration start time.
 * Bybit: `createdTime` = first-ever position on that symbol (stale after close/reopen).
 * Prefer `openTime` (current open), then update times; `createdTime` last.
 * Binance: typically only `updateTime`, which is correct for the current position.
 * Spot balances may use `timestamp` / nested `position` times.
 */
const getOpenedAt = (item) =>
  pickTimestamp(
    item?.openTime,
    item?.openedAt,
    item?.cTime,
    item?.entryTime,
    item?.timestamp,
    item?.uTime,
    item?.updateTime,
    item?.updatedTime,
    item?.time,
    item?.createdTime,
    item?.createdAt,
    item?.position?.openTime,
    item?.position?.updateTime,
    item?.position?.updatedTime,
    item?.position?.createdTime
  );

const formatOpenTimestamp = (timestamp) => {
  if (timestamp == null || timestamp === "") return null;

  let start = Number(timestamp);
  if (!Number.isFinite(start)) {
    const parsed = Date.parse(timestamp);
    if (!Number.isFinite(parsed)) return null;
    start = parsed;
  }
  if (start <= 0) return null;
  if (start < 1e12) start *= 1000;
  if (start < 946684800000) return null; // before 2000-01-01

  const date = new Date(start);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTradeSide = (item) => {
  const side = String(item?.side || "").toLowerCase();
  if (side === "buy" || side === "long") return "Long";
  if (side === "sell" || side === "short") return "Short";
  if (Number(item?.positionAmt) > 0) return "Long";
  if (Number(item?.positionAmt) < 0) return "Short";
  return item?.side || "—";
};

const formatMoney = (value, { prefix = "$", digits = 2 } = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${prefix}${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })}`;
};

const formatSignedMoney = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const sign = num > 0 ? "+" : num < 0 ? "-" : "";
  return `${sign}$${Math.abs(num).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatPnl = (value, { signed = false } = {}) => {
  if (value == null || value === "") return signed ? "—" : "0%";
  const num = Number(value);
  if (!Number.isFinite(num)) return signed ? "—" : "0%";
  const sign = signed && num > 0 ? "+" : "";
  return `${sign}${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const formatSize = (value) => {
  const num = Math.abs(Number(value));
  if (!Number.isFinite(num) || num === 0) return null;
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
};

const toNumber = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const num = toNumber(value);
    if (num != null) return num;
  }
  return null;
};

const getPnlToneClass = (value) => {
  const num = toNumber(value);
  if (num == null || num === 0) return "";
  return num < 0 ? "is-negative" : "is-positive";
};

/** Spot APIs often omit pnl fields — derive from entry/mark/size when needed. */
const resolveSpotMetrics = (item, isShort) => {
  const entry = firstNumber(
    item?.entryPrice,
    item?.avgPrice,
    item?.averagePrice,
    item?.costPrice,
    item?.purchasePrice,
    item?.avgEntryPrice
  );
  const mark = firstNumber(
    item?.markPrice,
    item?.currentPrice,
    item?.mark_price,
    item?.lastPrice,
    item?.price,
    item?.marketPrice
  );
  const size = firstNumber(
    item?.positionAmt,
    item?.qty,
    item?.quantity,
    item?.size,
    item?.executedQty
  );
  const absSize = size != null ? Math.abs(size) : null;

  let positionValue = firstNumber(
    item?.positionValue,
    item?.notional,
    item?.positionNotional,
    item?.marketValue,
    item?.value
  );
  if (positionValue == null && mark != null && absSize) {
    positionValue = mark * absSize;
  }

  let unrealized = firstNumber(
    item?.unRealizedProfit,
    item?.unrealizedProfit,
    item?.unrealizedPnl,
    item?.pnl,
    item?.unrealized,
    item?.uPnl
  );
  if (
    unrealized == null &&
    entry != null &&
    entry > 0 &&
    mark != null &&
    absSize
  ) {
    unrealized = isShort
      ? (entry - mark) * absSize
      : (mark - entry) * absSize;
  }

  let pnlPercentage = firstNumber(
    item?.pnlPercentage,
    item?.roe,
    item?.percentage,
    item?.unrealizedPnlPercentage,
    item?.pnlPercent
  );
  if (pnlPercentage == null && unrealized != null && entry != null && absSize) {
    const costBasis = Math.abs(entry * absSize);
    if (costBasis > 0) pnlPercentage = (unrealized / costBasis) * 100;
  }

  return {
    size: absSize,
    positionValue,
    unrealized,
    pnlPercentage,
  };
};

const getMarketTypeParam = (marketKey) =>
  marketKey === "spot" ? "spot" : "futures";

const PAGINATION_SINGLE_ROW_PX = 24;
const MOBILE_PAGINATION_MQ = "(max-width: 767px)";

const VerifyLiveTrade = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openPosition, setOpenPosition] = useState([]);
  const { selectedMarket, permissionType } = useDashboardMarket();
  const isMarketAllowed = permissionType === "both" || permissionType === selectedMarket;
  const [paginationOverflows, setPaginationOverflows] = useState(false);
  const sliderRef = useRef(null);
  const paginationObserverRef = useRef(null);
  const api = useApi();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, setCurrentPageTitle] = useAtom(globalCurrentPageTitle);

  const isOnBoardingComplete =
    user?.onboarding?.isCommunityCreated &&
    user?.onboarding?.connect &&
    user?.onboarding?.payment &&
    user?.onboarding?.subscribed &&
    user?.onboarding?.isVerified;

  const syncPaginationOverflow = (swiper) => {
    const root = sliderRef.current;
    const el = swiper?.pagination?.el;
    paginationObserverRef.current?.disconnect();
    paginationObserverRef.current = null;

    const measure = () => {
      if (
        !root ||
        !el ||
        !window.matchMedia(MOBILE_PAGINATION_MQ).matches
      ) {
        root?.style.removeProperty("--live-pagination-pad");
        setPaginationOverflows(false);
        return;
      }

      const height = el.offsetHeight;
      if (height > PAGINATION_SINGLE_ROW_PX) {
        root.style.setProperty(
          "--live-pagination-pad",
          `${height + 12}px`
        );
        setPaginationOverflows(true);
      } else {
        root.style.removeProperty("--live-pagination-pad");
        setPaginationOverflows(false);
      }
    };

    if (!el) {
      root?.style.removeProperty("--live-pagination-pad");
      setPaginationOverflows(false);
      return;
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    paginationObserverRef.current = observer;
  };

  const callApi = async ({ manual = false, market = selectedMarket } = {}) => {
    if (manual && isRefreshing) return;
    if (manual) setIsRefreshing(true);
    setLoading(true);
    setOpenPosition([]);
    try {
      // Skip API call if selected market doesn't match user's permissionType
      if (!(permissionType === "both" || permissionType === market)) {
        setOpenPosition([]);
        return;
      }
      const marketType = getMarketTypeParam(market);
      const response = await api(
        "post",
        `livetrade/getLiveTrade?marketType=${marketType}`
      );
      if (response?.data) {
        const openPositions = response.data?.[0]?.openPositions || [];
        if (user?.leaderCommunity && !isOnBoardingComplete) {
          setOpenPosition([]);
        } else {
          setOpenPosition(openPositions);
        }
      } else {
        setOpenPosition([]);
      }
    } catch (err) {
      setOpenPosition([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setIsRefreshing(false);
      }, 800);
    }
  };

  useEffect(() => {
    callApi({ market: selectedMarket });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, selectedMarket, permissionType]);

  useEffect(() => {
    if (!openPosition.length) {
      sliderRef.current?.style.removeProperty("--live-pagination-pad");
      setPaginationOverflows(false);
    }
  }, [openPosition.length]);

  useEffect(
    () => () => {
      paginationObserverRef.current?.disconnect();
    },
    []
  );

  const spotHideBelow = getSpotLiveTradeHideBelow(user);
  const visiblePositions = useMemo(() => {
    if (!isMarketAllowed) return [];
    if (selectedMarket !== "spot" || !spotHideBelow) return openPosition;
    return openPosition.filter((item) => {
      const spot = resolveSpotMetrics(item, getTradeSide(item) === "Short");
      const value = Number(spot?.positionValue);
      if (!Number.isFinite(value)) return true;
      return value >= spotHideBelow;
    });
  }, [openPosition, selectedMarket, spotHideBelow, isMarketAllowed]);

  const hasTrades = visiblePositions.length > 0;
  const selectedMarketLabel =
    MARKET_OPTIONS.find((option) => option.key === selectedMarket)?.label ||
    "Futures";

  return (
    <div
      className={`VerifyLiveTradeBox${
        hasTrades ? " VerifyLiveTradeBox--hasTrades" : ""
      }`}
    >
      <div className="VerifyLiveTradeBox__heading">
        <div className="VerifyLiveTradeBox__headingLeft">
          <p className="VerifyLiveTradeBox__title">
            Live Trades: <span>{visiblePositions.length}</span>
          </p>
          <SpotLiveTradeHideSelect selectedMarket={selectedMarket} />
        </div>

        <button
          type="button"
          className={`VerifyLiveTradeBox__refreshBtn${
            isRefreshing ? " is-refreshing" : ""
          }`}
          onClick={() => callApi({ manual: true })}
          disabled={isRefreshing}
          aria-label="Refresh live trades"
        >
          <img src={RefreshIcon} alt="" />
        </button>
      </div>

      <div
        ref={sliderRef}
        className={`tradeSlider verifyLiveTradeSlider${
          paginationOverflows ? " tradeSlider--paginationOverflow" : ""
        }`}
      >
        {!hasTrades ? (
          <div className="liveTradeCard liveTradeCard--empty">
            {loading ? (
              <div className="loading-4">
                <span className="span1"></span>
                <span className="span2"></span>
                <span className="span3"></span>
              </div>
            ) : (
              <p>{`No live ${selectedMarketLabel.toLowerCase()} trades`}</p>
            )}
          </div>
        ) : (
          <Swiper
            spaceBetween={14}
            slidesOffsetAfter={24}
            pagination={{ clickable: true }}
            modules={[Pagination]}
            onSwiper={syncPaginationOverflow}
            onResize={syncPaginationOverflow}
            onBreakpoint={syncPaginationOverflow}
            breakpoints={{
              320: { slidesPerView: 1.05 },
              480: { slidesPerView: 1.2 },
              768: { slidesPerView: 2.2 },
              1024: { slidesPerView: 2.8 },
              1199: { slidesPerView: 3.2 },
              1300: { slidesPerView: 3.5 },
              1391: { slidesPerView: 3.85 },
            }}
            className="my-swiper"
          >
            {visiblePositions.map((item, index) => {
              const isSpot = selectedMarket === "spot";
              const side = getTradeSide(item);
              const isShort = side === "Short";
              const spot = isSpot ? resolveSpotMetrics(item, isShort) : null;
              const pnl = isSpot
                ? spot.pnlPercentage
                : Number(item?.pnlPercentage) || 0;
              const unrealized = isSpot
                ? spot.unrealized
                : firstNumber(
                    item?.unRealizedProfit,
                    item?.unrealizedProfit,
                    item?.unrealizedPnl,
                    item?.pnl
                  );
              const leverage =
                item?.leverage ??
                item?.positionLeverage ??
                item?.leverageValue;
              const size = isSpot
                ? formatSize(spot.size)
                : formatSize(
                    item?.positionAmt ??
                      item?.qty ??
                      item?.quantity ??
                      item?.size
                  );
              const positionValue = isSpot ? spot.positionValue : null;
              const openedAt = getOpenedAt(item);
              const openDuration = formatOpenDuration(openedAt);
              const openTimestamp = formatOpenTimestamp(openedAt);

              return (
                <SwiperSlide
                  key={item.id || item.symbol || index}
                  className="slide"
                >
                  <div
                    className={`liveTradeCard${
                      isSpot ? " liveTradeCard--spot" : ""
                    }`}
                  >
                    {loading ? (
                      <div className="loading-4">
                        <span className="span1"></span>
                        <span className="span2"></span>
                        <span className="span3"></span>
                      </div>
                    ) : isSpot ? (
                      <>
                        <div className="liveTradeCard__top">
                          <div className="liveTradeCard__meta">
                            <p className="liveTradeCard__symbol">
                              {item?.symbol}
                            </p>
                            <div className="liveTradeCard__spotMeta">
                              <p className="liveTradeCard__leverage">
                                {size ? `Qty ${size}` : "—"}
                              </p>
                              <p className="liveTradeCard__leverage">
                                {positionValue != null
                                  ? `Investment ${formatMoney(positionValue)}`
                                  : "—"}
                              </p>
                            </div>
                          </div>
                          <span className="liveTradeCard__side liveTradeCard__side--spot">
                            Spot
                          </span>
                        </div>

                        <div className="liveTradeCard__bottom">
                          <div className="liveTradeCard__spotFooterMeta">
                            <p className="liveTradeCard__duration">
                              {openDuration || "—"}
                            </p>
                            {openTimestamp ? (
                              <p className="liveTradeCard__timestamp">
                                {openTimestamp}
                              </p>
                            ) : null}
                          </div>
                          <div className="liveTradeCard__pnlWrap">
                            <p
                              className={`liveTradeCard__pnl ${getPnlToneClass(
                                pnl
                              )}`}
                            >
                              {formatPnl(pnl, { signed: true })}
                            </p>
                            <p
                              className={`liveTradeCard__pnlCash ${getPnlToneClass(
                                unrealized
                              )}`}
                            >
                              {formatSignedMoney(unrealized)}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="liveTradeCard__top">
                          <div className="liveTradeCard__meta">
                            <p className="liveTradeCard__symbol">
                              {item?.symbol}
                            </p>
                            {leverage != null && leverage !== "" && (
                              <p className="liveTradeCard__leverage">
                                {`${leverage}`.toLowerCase().includes("x")
                                  ? leverage
                                  : `${leverage}x`}
                              </p>
                            )}
                          </div>
                          <span
                            className={`liveTradeCard__side ${
                              isShort
                                ? "liveTradeCard__side--short"
                                : "liveTradeCard__side--long"
                            }`}
                          >
                            {side}
                          </span>
                        </div>

                        <div className="liveTradeCard__bottom">
                          {openDuration ? (
                            <p className="liveTradeCard__duration">
                              {openDuration}
                            </p>
                          ) : (
                            <span />
                          )}
                          <p
                            className={`liveTradeCard__pnl ${getPnlToneClass(
                              pnl
                            )}`}
                          >
                            {formatPnl(pnl)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
      </div>

      <div className="VerifyLiveTradeBox__footer">
        <button
          type="button"
          className="seeVerifyTradingHistoryBtn"
          onClick={() => {
            dispatch(setCurrentTab("verifiedTradeHistory"));
            navigate(`/${user?.role}/verifyTradeListing`);
            setCurrentPageTitle("");
          }}
        >
          See history &gt;
        </button>
      </div>
    </div>
  );
};

export default VerifyLiveTrade;
